import { useQuery } from "@tanstack/react-query";
import { StatusPanel, TrendPanel } from "../../components/dashboard";
import { AvgBadge, DxCard, KpiTile, RankList, UnitTable } from "../../components/dx";
import { fetchDocTypes, placeKey, SLOW_THRESHOLD_SECONDS } from "../../lib/dashboard-data";
import { duration, num } from "../../lib/format";
import { rangeKey } from "../../lib/stats-range";
import type { DashCtx } from "./ctx";

/** เพดาน limit ของ /stats/document-types (analytics.maxTopLimit) — พอสำหรับทุกชนิดในแคตตาล็อก */
const ALL_TYPES = 100;

const pct = (a: number, b: number) => (b > 0 ? `${(Math.round((a / b) * 1000) / 10).toLocaleString("lo-LA")}%` : "—");

/**
 * ແຖບ ສະຖິຕິ (mockup `dx_stats.js`) — ทุกบริการ · สถานะที่กำลังดำเนิน · เวลาดำเนินงานแยกตามพื้นที่
 * ส่วนของ mockup ที่ระบบไม่มีข้อมูล (เหตุผลที่ส่งคืน, ผู้ยื่นลาว/ต่างด้าว, การรับเอกสารแบบดิจิทัล/กระดาษ) ไม่ทำ
 */
export function StatsTab({ ctx }: { ctx: DashCtx }) {
  const { range, place, here, data, breakdown, unit, level } = ctx;
  const s = data.summary.data;
  const types = useQuery({
    queryKey: ["stats", "document-types", `${rangeKey(range)}|${placeKey(place)}`, ALL_TYPES],
    queryFn: () => fetchDocTypes(range, place, ALL_TYPES),
  });
  const list = [...(types.data ?? [])].sort((a, b) => b.requests - a.requests);
  const totalReq = s?.submitted ?? list.reduce((a, d) => a + d.requests, 0);
  const slow = (unit?.rows ?? [])
    .filter((r) => r.avg_processing_seconds > 0)
    .sort((a, b) => b.avg_processing_seconds - a.avg_processing_seconds);

  return (
    <>
      {types.error ?? breakdown.error ? (
        <div className="alert-error dx-gap-b">ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ: {(types.error ?? breakdown.error)?.message}</div>
      ) : null}
      <div className="dx-kpis dx-kpis--5">
        <KpiTile
          label={range.kind === "today" ? "ຄຳຮ້ອງໃໝ່" : "ຈຳນວນຄຳຮ້ອງ"}
          value={s ? num(s.submitted) : undefined}
          unit="ຄຳຮ້ອງ"
          tone="navy"
          deltaPct={s?.submitted_delta_pct}
        />
        <KpiTile label="ເອກະສານທີ່ອອກແລ້ວ" value={s ? num(s.issued) : undefined} unit="ສະບັບ" tone="green" deltaPct={s?.issued_delta_pct} />
        <KpiTile
          label="ອັດຕາອອກເອກະສານ"
          value={s ? pct(s.issued, s.submitted) : undefined}
          tone="teal"
          hint="ຄຳຮ້ອງທີ່ຍື່ນໃນຊ່ວງນີ້ ແລະ ອອກເອກະສານແລ້ວ"
        />
        <KpiTile
          label="ເວລາດຳເນີນງານສະເລ່ຍ"
          value={s ? duration(s.avg_processing_seconds) : undefined}
          tone="ink"
          hint={s && s.avg_processing_seconds <= 0 ? "ຍັງບໍ່ມີໃບທີ່ອອກໃນຊ່ວງນີ້" : "ຍື່ນ → ອອກເອກະສານ · ເກີນ 7 ມື້ ຖືວ່າຊັກຊ້າ"}
        />
        <KpiTile
          label="ກຳລັງດຳເນີນການ"
          value={s ? num(s.pending) : undefined}
          unit="ລາຍການ"
          tone="red"
          hint={s ? `ເກີນ 7 ມື້ ${num(s.overdue_over_7d)} · ສະຖານະຕອນນີ້` : undefined}
          to="/requests"
        />
      </div>

      <div className="dx-gap">
        <DxCard flush title="ຕາຕະລາງທຸກບໍລິການ" sub={`${here.name} · ${ctx.period} · ລຽງຕາມຈຳນວນຄຳຮ້ອງ`}>
          <div className="table-scroll">
            <table className="data-table dx-table">
              <thead>
                <tr>
                  <th>ລະຫັດ</th>
                  <th>ບໍລິການ</th>
                  <th className="is-right">ຈຳນວນຄຳຮ້ອງ</th>
                  <th className="is-right">ສ່ວນແບ່ງ</th>
                  <th className="is-right">ອອກແລ້ວ</th>
                  <th className="is-right">ອັດຕາອອກ</th>
                </tr>
              </thead>
              <tbody>
                {types.isPending ? (
                  <tr>
                    <td colSpan={6} className="data-table__empty">
                      ກຳລັງໂຫຼດ…
                    </td>
                  </tr>
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="data-table__empty">
                      ຍັງບໍ່ມີຄຳຮ້ອງໃນຊ່ວງນີ້
                    </td>
                  </tr>
                ) : (
                  list.map((d) => (
                    <tr key={d.document_type_id}>
                      <td className="num">{d.code}</td>
                      <td className="dx-table__name">{d.name_lo}</td>
                      <td className="is-right num">{num(d.requests)}</td>
                      <td className="is-right num">{pct(d.requests, totalReq)}</td>
                      <td className="is-right num">{num(d.issued)}</td>
                      <td className="is-right num">{pct(d.issued, d.requests)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <p className="dx-note dx-note--pad">ສະແດງສະເພາະບໍລິການທີ່ມີຄຳຮ້ອງໃນຊ່ວງນີ້</p>
        </DxCard>
      </div>

      <div className="grid2 grid2--even dx-gap">
        <StatusPanel items={data.statuses.data ?? []} pending={data.statuses.isPending} />
        {unit ? (
          <DxCard title={`ເວລາດຳເນີນງານ ແຍກຕາມ${unit.word}`} sub="ຊ້າສຸດຢູ່ເທິງ · ຍື່ນ → ອອກເອກະສານ">
            <RankList
              emptyLabel={breakdown.isPending ? "ກຳລັງໂຫຼດ…" : "ຍັງບໍ່ມີໃບທີ່ອອກໃນຊ່ວງນີ້"}
              rows={slow.slice(0, 10).map((r) => ({
                key: r.place_id ?? "unplaced",
                label: unit.nameOf(r),
                sub: r.avg_processing_seconds > SLOW_THRESHOLD_SECONDS ? "ຊັກຊ້າ (ເກີນ 7 ມື້)" : undefined,
                value: r.avg_processing_seconds,
                text: duration(r.avg_processing_seconds),
                share: null,
                color: r.avg_processing_seconds > SLOW_THRESHOLD_SECONDS ? "var(--warning)" : "var(--success)",
                ...(unit.onDrill && r.place_id ? { onClick: () => unit.onDrill?.(r.place_id as string) } : {}),
              }))}
            />
            {s ? (
              <p className="dx-note">
                ສະເລ່ຍທັງ{here.name}: <AvgBadge seconds={s.avg_processing_seconds} />
              </p>
            ) : null}
          </DxCard>
        ) : (
          <TrendPanel rows={data.daily.data ?? []} weekly={data.weekly} pending={data.daily.isPending} fees={ctx.finance} />
        )}
      </div>

      {unit ? (
        <>
          <div className="dx-gap">
            <UnitTable
              u={unit}
              here={here.name}
              level={level === "none" ? "village" : level}
              summary={s}
              pending={breakdown.isPending || data.summary.isPending}
              title={`ປຽບທຽບ${unit.word}`}
            />
          </div>
          <div className="dx-gap">
            <TrendPanel rows={data.daily.data ?? []} weekly={data.weekly} pending={data.daily.isPending} fees={ctx.finance} />
          </div>
        </>
      ) : null}
    </>
  );
}
